import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, ClipboardList, Star, LogOut, Shield } from 'lucide-react';
import HelpCenter from './HelpCenter';
import WhatsNewBanner from './WhatsNewBanner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_15eae824-fd2f-440b-b0c8-e9a3a28c27b6/artifacts/19uczlft_GrowthHubElectric.png';

// Compact role pills shown under the signed-in user's name. Short labels keep
// the sidebar tidy; the full title shows on hover.
const ROLE_LABELS = {
  super_admin: 'Super Admin',
  template_admin: 'Templates',
  reports_admin: 'Reports',
  review_initiator: 'AM / DOR',
};
const ROLE_TITLES = {
  super_admin: 'Super Administrator — full control of users, roles, templates, and data',
  template_admin: 'Template Administrator — edit Tactical and Facility templates',
  reports_admin: 'Reports Administrator — access analytics and API endpoints',
  review_initiator: 'Review Initiator — can initiate 5-Star reviews and act as an Area Manager',
};
const ROLE_PILL_STYLES = {
  super_admin: 'bg-rose-50 text-rose-700 border-rose-200',
  template_admin: 'bg-violet-50 text-violet-700 border-violet-200',
  reports_admin: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  review_initiator: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
};

const NavSection = ({ label, children }) => (
  <div className="mb-6">
    <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[1.5px] text-slate-400">{label}</div>
    {children}
  </div>
);

const NavRow = ({ to, icon: Icon, label, testId, accent = 'cyan', end = false }) => {
  const activeStyle = accent === 'magenta'
    ? 'bg-gradient-to-r from-fuchsia-500/10 to-fuchsia-500/0 text-fuchsia-700 shadow-[inset_3px_0_0_#d500f9]'
    : 'bg-gradient-to-r from-cyan-500/10 to-cyan-500/0 text-cyan-700 shadow-[inset_3px_0_0_#00b8d4]';
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition mb-0.5 ${
          isActive ? activeStyle : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </NavLink>
  );
};

const GrowthHubLayout = ({ children }) => {
  const { user, logout, adminRoles } = useAuth();
  const navigate = useNavigate();
  const hasAdminAccess = (adminRoles || []).length > 0;

  const initials = (user?.display_name || user?.email || '?')
    .split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      {/* Sidebar */}
      <aside
        className="fixed left-0 top-0 bottom-0 w-[264px] bg-white border-r border-slate-200 flex flex-col z-40"
        data-testid="growth-hub-sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-200 flex justify-center bg-white">
          <img src={LOGO_URL} alt="TMC Growth Hub" className="w-[200px] h-auto" />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <NavSection label="Main">
            <NavRow end to="/" icon={LayoutDashboard} label="Growth Hub" testId="nav-home" accent="cyan" />
          </NavSection>
          <NavSection label="Services">
            <NavRow to="/tacticals" icon={ClipboardList} label="Tacticals" testId="nav-tacticals" accent="cyan" />
            <NavRow to="/five-star" icon={Star} label="5 Star Feedback" testId="nav-five-star" accent="magenta" />
          </NavSection>
          {hasAdminAccess && (
            <NavSection label="Manage">
              <NavRow to="/admin" icon={Shield} label="Admin" testId="nav-admin" accent="cyan" />
            </NavSection>
          )}
        </nav>

        <div className="px-3 pb-4 pt-3 border-t border-slate-200">
          <div className="px-1 mb-2 flex justify-center">
            <HelpCenter />
          </div>
          <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-slate-50">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #00b8d4, #d500f9)' }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate" data-testid="sidebar-user-name">
                {user?.display_name || user?.email}
              </div>
              <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
              {Array.isArray(adminRoles) && adminRoles.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid="sidebar-roles">
                  {adminRoles.map(r => (
                    <span
                      key={r}
                      data-testid={`role-pill-${r}`}
                      title={ROLE_TITLES[r] || r}
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider whitespace-nowrap border ${ROLE_PILL_STYLES[r] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
                    >
                      {ROLE_LABELS[r] || r}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              data-testid="sidebar-logout"
              className="w-8 h-8 rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800 flex items-center justify-center transition flex-shrink-0"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-[264px] min-h-screen">
        {children}
      </main>
    </div>
  );
};

export default GrowthHubLayout;
